import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { CurrentUser } from '@/api/client';
import { useCompany } from '@/stores/company';
import { useLock } from '@/stores/lock';

/**
 * Covers the app while it is locked. The work underneath stays exactly as it
 * was — a half-filled form is still there after unlocking. "Sign out" is for
 * when someone else needs the computer.
 */
export function LockScreen({ user, onSignOut }: { user: CurrentUser; onSignOut: () => void }) {
  const unlock = useLock((s) => s.unlock);
  const companyName = useCompany((s) => s.company.nameEn);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setError(null);
    try {
      await unlock(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not unlock. Try again.');
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lockscreen" role="dialog" aria-modal="true" aria-labelledby="lockscreen-title">
      <form className="lockscreen__card" onSubmit={submit}>
        <p className="lockscreen__company">{companyName}</p>
        <h1 id="lockscreen-title">Screen locked</h1>
        <p className="muted">
          Locked because nobody used it for a while. Enter the password for <strong>{user.fullName}</strong>{' '}
          to carry on where you left off.
        </p>
        <label className="field">
          <span className="field__label">Password</span>
          <input
            ref={inputRef}
            className="field__input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        {error && (
          <p className="errornote" role="alert">
            <span aria-hidden="true">⚠ </span>
            {error}
          </p>
        )}
        <button type="submit" className="btn btn--primary" disabled={busy || !password}>
          {busy ? 'Checking…' : 'Unlock'}
        </button>
        <button type="button" className="linkbutton lockscreen__signout" onClick={onSignOut}>
          Not {user.fullName}? Sign out
        </button>
      </form>
    </div>
  );
}
